import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, PlayCircle } from "lucide-react";

const getYouTubeVideoId = (videoId, youtubeUrl) => {
    if (videoId) return videoId;
    if (!youtubeUrl) return "";

    try {
        const url = new URL(youtubeUrl);

        if (url.hostname.includes("youtu.be")) {
            return url.pathname.replace("/", "");
        }

        return url.searchParams.get("v") || "";
    } catch {
        return "";
    }
};

function GuideVideo({
    videoId,
    youtubeUrl,
    url,
    title = "Guide video",
    thumbnailUrl,
    className = "",
}) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [thumbnailSrc, setThumbnailSrc] = useState("");
    const effectiveYoutubeUrl = youtubeUrl || url;
    const resolvedVideoId = useMemo(
        () => getYouTubeVideoId(videoId, effectiveYoutubeUrl),
        [videoId, effectiveYoutubeUrl],
    );
    const fallbackThumbnailUrl = resolvedVideoId
        ? `https://i.ytimg.com/vi/${resolvedVideoId}/hqdefault.jpg`
        : "";
    const watchUrl =
        effectiveYoutubeUrl ||
        (resolvedVideoId
            ? `https://www.youtube.com/watch?v=${resolvedVideoId}`
            : "");
    const embedUrl = resolvedVideoId
        ? `https://www.youtube-nocookie.com/embed/${resolvedVideoId}?autoplay=1&rel=0`
        : "";

    useEffect(() => {
        setIsPlaying(false);
        setThumbnailSrc(thumbnailUrl || fallbackThumbnailUrl);
    }, [fallbackThumbnailUrl, resolvedVideoId, thumbnailUrl]);

    const handleThumbnailError = () => {
        if (fallbackThumbnailUrl && thumbnailSrc !== fallbackThumbnailUrl) {
            setThumbnailSrc(fallbackThumbnailUrl);
            return;
        }

        setThumbnailSrc("");
    };

    return (
        <div
            className={`mx-auto mt-5 w-full max-w-4xl overflow-hidden rounded-2xl border border-indigo-100 bg-indigo-50/50 shadow-sm ${className}`}
        >
            <div className="relative aspect-video w-full bg-gradient-to-br from-indigo-50 via-white to-blue-50">
                {isPlaying && embedUrl ? (
                    <iframe
                        src={embedUrl}
                        title={title}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsPlaying(true)}
                        disabled={!embedUrl}
                        aria-label={`Play ${title}`}
                        className="group flex h-full w-full items-center justify-center px-4 text-center focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-200 disabled:cursor-not-allowed"
                    >
                        {thumbnailSrc && (
                            <img
                                src={thumbnailSrc}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="lazy"
                                aria-hidden="true"
                                onError={handleThumbnailError}
                            />
                        )}
                        <div className="absolute inset-0 bg-slate-950/35 transition group-hover:bg-slate-950/45" />
                        <div className="relative z-10 flex max-w-md flex-col items-center">
                            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-950/30 transition group-hover:scale-105 group-hover:bg-indigo-700 group-disabled:bg-slate-300 group-disabled:shadow-none">
                                <PlayCircle
                                    size={34}
                                    aria-hidden="true"
                                />
                            </span>
                            {title && (
                                <p className="mt-4 text-base font-bold text-white drop-shadow-sm sm:text-lg">
                                    {title}
                                </p>
                            )}
                            <p className="mt-2 text-sm font-semibold text-indigo-50 drop-shadow-sm">
                                Click play to load the video.
                            </p>
                        </div>
                    </button>
                )}
            </div>

            {watchUrl && (
                <div className="flex justify-end border-t border-indigo-100 bg-white/80 px-4 py-3">
                    <a
                        href={watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Watch ${title} on YouTube`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700 transition hover:text-indigo-800 hover:underline"
                    >
                        Watch on YouTube
                        <ExternalLink size={14} aria-hidden="true" />
                    </a>
                </div>
            )}
        </div>
    );
}

export default GuideVideo;
